import { Button } from "@gettactic/components/src/primitives/button";
import React from "react";
import type {
  RegionalImportSnapshot,
  RetryBounds,
  RetryScope
} from "../../../../utils/requestRegionalFloorplan";
export function ImportRegionProgress({
  snapshot,
  selected,
  onSelect,
  onRetry,
  onResume,
  busy,
  applying,
  scope,
  onScope,
  drawing,
  onDraw,
  bounds
}: {
  snapshot: RegionalImportSnapshot;
  selected: string;
  onSelect: (id: string) => void;
  onRetry: () => void;
  onResume: () => void;
  busy: boolean;
  applying: boolean;
  scope: RetryScope;
  onScope: (scope: RetryScope) => void;
  drawing: boolean;
  onDraw: () => void;
  bounds?: RetryBounds;
}): React.ReactElement {
  return (
    <div className="space-y-2 rounded border p-3">
      <p className="text-sm">
        {snapshot.status === "completed"
          ? "All areas are ready. Select an area to reconstruct it again."
          : "Preview of completed areas. All areas must finish before applying."}
      </p>
      <fieldset className="flex flex-wrap gap-2" aria-label="Floorplan areas">
        {snapshot.parts.map((p) => (
          <button
            type="button"
            key={p.id}
            aria-pressed={selected === p.id}
            onClick={() => onSelect(p.id)}
            className={`rounded border px-2 py-1 text-sm ${selected === p.id ? "border-blue-600 bg-blue-50" : ""}`}
          >
            {p.label} · {p.status.replace("_", " ")}
            {p.elapsedMs !== undefined
              ? ` · ${Math.round(p.elapsedMs / 1000)}s`
              : ""}
          </button>
        ))}
      </fieldset>
      {snapshot.parts
        .filter((p) => p.error)
        .map((p) => (
          <p key={p.id} className="text-red-600 text-xs">
            {p.label}: {p.error}
          </p>
        ))}
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm">
          Retry content{" "}
          <select
            aria-label="Retry content"
            className="rounded border p-1"
            value={selected === "perimeter" ? "walls" : scope}
            disabled={
              busy ||
              applying ||
              selected === "perimeter" ||
              snapshot.parts.find((p) => p.id === selected)?.hasResult === false
            }
            onChange={(e) => onScope(e.target.value as RetryScope)}
          >
            <option value="all">Walls and furniture</option>
            <option value="walls">Walls only</option>
            <option value="furniture">Furniture only</option>
          </select>
        </label>
        <Button
          aria-pressed={drawing}
          onClick={onDraw}
          disabled={
            busy ||
            applying ||
            snapshot.parts.some((p) => !p.patch && p.status !== "completed")
          }
        >
          {drawing ? "Cancel box selection" : "Draw a retry box"}
        </Button>
      </div>
      {bounds && (
        <p className="text-sm">
          Selected box: {bounds.width} × {bounds.height} pixels at ({bounds.x},{" "}
          {bounds.y}).
        </p>
      )}
      {drawing && (
        <p className="text-sm">
          Drag on the source image. Minimum 16 × 16 pixels. Escape cancels
          selection.
        </p>
      )}
      <p className="text-neutral-500 text-xs">
        Retry replaces only the selected area and keeps the others cached for 30
        minutes. Box retries affect interior walls and furniture; use Exterior
        walls to retry the perimeter.
      </p>
      <details className="text-xs">
        <summary>Processing details</summary>
        <table className="mt-2 w-full text-left">
          <thead>
            <tr>
              <th>Area</th>
              <th>Time</th>
              <th>Input tokens</th>
              <th>Output tokens</th>
              <th>Reasoning tokens</th>
            </tr>
          </thead>
          <tbody>
            {snapshot.parts.map((p) => (
              <tr key={p.id}>
                <td>{p.label}</td>
                <td>
                  {p.elapsedMs === undefined
                    ? "—"
                    : `${Math.round(p.elapsedMs / 1000)}s`}
                </td>
                <td>{p.usage?.input_tokens ?? "—"}</td>
                <td>{p.usage?.output_tokens ?? "—"}</td>
                <td>
                  {p.usage?.output_tokens_details?.reasoning_tokens ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {snapshot.local && (
          <p className="mt-2">
            Local preparation: {Math.round(snapshot.local.preprocessMs)}ms ·
            Last merge and conversion: {Math.round(snapshot.local.mergeMs)}ms ·
            Wall junctions repaired locally: {snapshot.local.repairs}
          </p>
        )}
      </details>
      {snapshot.status === "in_progress" && !busy ? (
        <Button onClick={onResume} disabled={applying}>
          Resume progress
        </Button>
      ) : (
        <Button onClick={onRetry} disabled={busy || applying || !selected}>
          Retry selected area
        </Button>
      )}
    </div>
  );
}
export function ImportRegionSourceOverlay({
  snapshot,
  selected,
  onSelect,
  width,
  height
}: {
  snapshot: {
    parts: Pick<
      RegionalImportSnapshot["parts"][number],
      "id" | "label" | "bounds"
    >[];
  };
  selected: string;
  onSelect: (id: string) => void;
  width: number;
  height: number;
}) {
  return (
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox={`0 0 ${width} ${height}`}
      aria-label="Select an area of the source floorplan"
    >
      {snapshot.parts
        .filter((p) => p.bounds)
        .map((p) => {
          const b = p.bounds!;
          return (
            <g key={p.id}>
              {/* biome-ignore lint/a11y/useSemanticElements: SVG hit target; equivalent HTML area buttons are also provided. */}
              <rect
                x={b.x}
                y={b.y}
                width={b.width}
                height={b.height}
                fill={selected === p.id ? "#2563eb" : "transparent"}
                fillOpacity={0.12}
                stroke="#2563eb"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
                role="button"
                aria-label={`Select ${p.label}`}
                aria-pressed={selected === p.id}
                tabIndex={0}
                className="cursor-pointer"
                onClick={() => onSelect(p.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect(p.id);
                  }
                }}
              />
              <text
                x={b.x + 8}
                y={b.y + 22}
                fill="#1d4ed8"
                fontSize={Math.max(width, height) / 50}
                pointerEvents="none"
              >
                {p.label}
              </text>
            </g>
          );
        })}
    </svg>
  );
}
